import { ChatMessage as ChatMessageType } from "@/types/chat";
import { cn } from "@/utils/cn";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Bot, Clock, User } from "lucide-react";
import React from "react";

interface ChatMessageProps {
  message: ChatMessageType;
  className?: string;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  className = "",
}) => {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";

  const messageClasses = cn(
    "flex items-start space-x-3 p-4 rounded-lg",
    isUser ? "justify-end" : "justify-start",
    className
  );

  const bubbleClasses = cn(
    "max-w-[80%] p-3 rounded-lg",
    isUser
      ? "bg-primary-600 text-white rounded-br-none"
      : "bg-gray-100 text-gray-900 rounded-bl-none"
  );

  const iconClasses = cn(
    "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
    isUser ? "bg-primary-600" : "bg-gray-300"
  );

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return format(date, "HH:mm", { locale: ptBR });
    } catch {
      return "00:00";
    }
  };

  return (
    <div className={messageClasses}>
      {isAssistant && (
        <div className={iconClasses}>
          <Bot size={16} className="text-gray-600" />
        </div>
      )}

      <div className="flex flex-col space-y-1">
        <div className={bubbleClasses}>
          <p className="text-sm whitespace-pre-wrap break-words">
            {message.content}
          </p>
        </div>

        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <Clock size={12} />
          <span>{formatTimestamp(message.timestamp)}</span>
          {isUser && (
            <>
              <span>•</span>
              <span>ID: {message.id.slice(0, 8)}...</span>
            </>
          )}
        </div>
      </div>

      {isUser && (
        <div className={iconClasses}>
          <User size={16} className="text-white" />
        </div>
      )}
    </div>
  );
};
